import { Avatar, Button, Divider, Stack, Typography } from '@mui/material';
import { ArrowLeftIcon, InfoCircleIcon } from 'assets';
import { PopupConfirm, TYPE_ICON } from 'components';
import { DATE_TIME_FORMAT } from 'constants/date';
import { SYS_MESS } from 'constants/systemMessage';
import { AcceptExtensionReductionModal } from 'features/bookings/components';
import { ACTION_TYPE, JOB_STATUS, MODAL_ACTION } from 'models';
import { SITTER_STATUS_LABEL } from 'models/overview';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  cancelJobAction,
  getJobDetailAction,
} from 'store/reducers/job/actionTypes';
import { updateACRequestSitterStatus } from 'store/reducers/overview';
import { theme } from 'theme';
import {
  convertDobToAge,
  differenceInHours,
  formatDate,
  formatPrice,
  pluralString,
  toCapitalize,
} from 'utils';
import {
  mapBookingColor,
  mapBookingTitle,
  mapButtonTitle,
} from '../../helpers';
import {
  FeeItemNumber,
  FeeItemText,
  StyledBackgroundOverlay,
  StyledBookingDetailWrapper,
  StyledBoxChildrenWrapper,
  StyledContentWrapper,
  StyledJobWrapper,
  StyledSitterWrapper,
  TotalAmountNumber,
  TotalAmountText,
} from './styles';

export function JobDetail() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { jobDetail } = useAppSelector((state) => state.jobs);
  const [modalAction, setModalAction] = useState<MODAL_ACTION<ACTION_TYPE>>();

  const { id } = useParams();

  const jobExtendPending = jobDetail?.JobExtensions?.find(
    (item) => item.status === 'pending',
  );

  // const jobSurchargePending = jobDetail?.job_surcharges?.find(
  //   (item) => item.status === 'pending',
  // );

  const showButtons = ![
    JOB_STATUS.CANCELLED,
    JOB_STATUS.STARTED,
    JOB_STATUS.PENDING_COMPLETED,
    JOB_STATUS.COMPLETED,
  ].includes(jobDetail?.status);

  const handlePaymentJob = () => {
    navigate(
      `${APP_ROUTES.JOB.PAYMENT.to}?jobId=${jobDetail.id}&isTrial=${jobDetail.is_trial_job}`,
    );
  };

  const onSubmitCallback = () => {
    dispatch(
      cancelJobAction({
        data: { id: jobDetail.id },
        onSuccess: () => {
          setModalAction({ type: ACTION_TYPE.CANCELLED });
        },
      }),
    );
  };

  const onCloseModalAction = () => {
    setModalAction(undefined);
  };

  const onClickButton = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    callback: any,
  ) => {
    e.preventDefault();
    callback?.();
  };

  const getAddressInformation = () => {
    const { street, unit_number, block_number, postal_code } =
      jobDetail.address;
    return `${unit_number}, ${block_number} ${street}, Singapore ${postal_code}`;
  };

  const renderSurchargeName = (textExtended: string) => {
    if (textExtended.includes('(extended')) {
      const parts = textExtended.split('(extended');
      return (
        <>
          {parts[0]}
          <br />
          &nbsp;&nbsp;&nbsp;&nbsp;(Extended{parts[1]}
        </>
      );
    }
    return textExtended;
  };

  useEffect(() => {
    dispatch(getJobDetailAction({ data: { id } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!jobDetail) return;
  return (
    <StyledBookingDetailWrapper spacing={2}>
      <StyledBackgroundOverlay>
        <NavLink to={APP_ROUTES.JOB.to}>
          <ArrowLeftIcon sx={{ color: theme.palette.white.light }} />
          <Typography lineHeight="21px" variant="Web_Label_16">
            Back to Bookings
          </Typography>
        </NavLink>
      </StyledBackgroundOverlay>
      <StyledContentWrapper gap={2}>
        <StyledJobWrapper spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent={{ xs: 'start', md: 'space-between' }}
            spacing={{ xs: 4, md: 0 }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1, md: 0.5 }}
            >
              <Typography variant="Web_Title_18">{jobDetail.number}</Typography>
            </Stack>
            <Typography
              variant="Web_Label_16"
              color={mapBookingColor[jobDetail.status as JOB_STATUS]}
            >
              {jobDetail.status !== JOB_STATUS.CANCELLED &&
              jobDetail.status !== JOB_STATUS.COMPLETED
                ? jobExtendPending
                  ? 'Pending parent confirmation'
                  : mapBookingTitle[jobDetail.status]
                : mapBookingTitle[jobDetail.status]}
            </Typography>
          </Stack>
          <Divider />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 4, md: 14 }}
          >
            <Stack spacing={{ xs: 1 }}>
              <Typography
                variant="Web_Label_16"
                color={theme.palette.grey3.dark}
              >
                Start Date
              </Typography>
              {jobDetail.JobExtensions?.length > 0 && (
                <Typography variant="Web_Label_16">
                  {formatDate(jobDetail?.start_datetime, {
                    format: DATE_TIME_FORMAT.GENERAL,
                  })}
                </Typography>
              )}

              <Typography
                variant="Web_Label_16"
                sx={
                  jobDetail.JobExtensions?.length > 0
                    ? { textDecoration: 'line-through', color: '#3C3C4354' }
                    : {}
                }
              >
                {formatDate(jobDetail?.start_datetime, {
                  format: DATE_TIME_FORMAT.GENERAL,
                })}
              </Typography>
            </Stack>
            <Stack spacing={{ xs: 1 }}>
              <Typography
                variant="Web_Label_16"
                color={theme.palette.grey3.dark}
              >
                End Date
              </Typography>
              {jobDetail.JobExtensions?.length > 0 && (
                <Typography variant="Web_Label_16">
                  {formatDate(jobDetail.JobExtensions[0]?.end_datetime, {
                    format: DATE_TIME_FORMAT.GENERAL,
                  })}
                </Typography>
              )}
              <Typography
                variant="Web_Label_16"
                sx={
                  jobDetail.JobExtensions?.length > 0
                    ? { textDecoration: 'line-through', color: '#3C3C4354' }
                    : {}
                }
              >
                {formatDate(jobDetail?.end_datetime, {
                  format: DATE_TIME_FORMAT.GENERAL,
                })}
              </Typography>
            </Stack>
            <Stack spacing={{ xs: 1 }}>
              <Typography
                variant="Web_Label_16"
                color={theme.palette.grey3.dark}
              >
                Duration
              </Typography>
              {jobDetail.JobExtensions?.length > 0 && (
                <Typography variant="Web_Label_16">
                  {pluralString(
                    'hour',
                    jobExtendPending
                      ? Number(
                          differenceInHours(
                            jobExtendPending?.end_datetime,
                            jobDetail?.start_datetime,
                          ),
                        )
                      : jobDetail.total_hours,
                  )}
                </Typography>
              )}
              <Typography
                variant="Web_Label_16"
                sx={
                  jobDetail.JobExtensions?.length > 0
                    ? { textDecoration: 'line-through', color: '#3C3C4354' }
                    : {}
                }
              >
                {pluralString('hour', jobDetail.total_hours)}
              </Typography>
            </Stack>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
              Address
            </Typography>
            <Typography variant="Web_Label_16" textTransform={'capitalize'}>
              {getAddressInformation()}
            </Typography>
          </Stack>
          <StyledBoxChildrenWrapper>
            {jobDetail.children.map((item) => (
              <Stack
                key={item.id}
                direction={'row'}
                alignItems={'center'}
                spacing={1}
              >
                {item.picture ? (
                  <img src={item.picture} className="pictureChildren" alt="" />
                ) : (
                  <Avatar className="avatarChildren">
                    {item.full_name.charAt(0)}
                  </Avatar>
                )}

                <Stack>
                  <Typography className="nameChildren">
                    {item.full_name}
                  </Typography>
                  <Typography className="ageChildren">
                    {convertDobToAge(item.date_of_birth)}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </StyledBoxChildrenWrapper>
          <Divider />
          <Stack>
            <Typography
              fontSize={18}
              fontWeight={700}
              sx={{ opacity: 0.5 }}
              pb={1}
            >
              Fees Summary
            </Typography>
            <Stack spacing={2.25}>
              <Stack spacing={1.75}>
                <Stack spacing={1}>
                  <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    spacing={2}
                  >
                    <FeeItemText color="#6D6D6D">Job Fee</FeeItemText>
                    <FeeItemNumber color="##231F20">
                      {formatPrice(
                        jobExtendPending
                          ? jobDetail.total + +jobExtendPending.total
                          : jobDetail.total,
                      )}
                    </FeeItemNumber>
                  </Stack>
                  {jobDetail.job_surcharges?.map((s) => (
                    <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      spacing={2.25}
                    >
                      <FeeItemText color="#6D6D6D">
                        {renderSurchargeName(s.description)}
                      </FeeItemText>
                      <FeeItemNumber color="##231F20">
                        {formatPrice(s.parent_rate)}
                      </FeeItemNumber>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <TotalAmountText>Total Amount</TotalAmountText>
                  <TotalAmountNumber>
                    {formatPrice(
                      (jobExtendPending
                        ? jobDetail.total + +jobDetail.JobExtensions[0].total
                        : jobDetail.total) +
                        (jobDetail.job_surcharges?.length > 0
                          ? jobDetail.job_surcharges?.reduce(
                              (sum, adj) => sum + adj.parent_rate,
                              0,
                            )
                          : 0),
                    )}
                  </TotalAmountNumber>
                </Stack>
                {jobExtendPending && (
                  <Stack direction={'row'} spacing={1}>
                    <InfoCircleIcon
                      sx={{ color: theme.palette.primary.main, fontSize: 20 }}
                    />
                    <Typography variant="Web_Label_14" color="primary.main">
                      Awaiting parent confirmation and payment for new fee.
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
          <Divider />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent={'space-between'}
            alignItems={'center'}
            spacing={{ xs: 1, md: 0 }}
          >
            <Typography variant="Web_Label_16" color={theme.palette.grey3.dark}>
              Created on:&nbsp;
              {formatDate(jobDetail.createdAt, {
                format: DATE_TIME_FORMAT.NORMAL_DATE,
              })}
            </Typography>
            {showButtons && (
              <Stack direction={'row'} spacing={1}>
                <Button
                  variant="text"
                  onClick={(e) =>
                    onClickButton(
                      e,
                      setModalAction({ type: ACTION_TYPE.CANCEL }),
                    )
                  }
                >
                  Cancel Booking
                </Button>
                {jobExtendPending !== undefined ? (
                  <Button
                    variant="contained"
                    onClick={(e) =>
                      onClickButton(
                        e,
                        setModalAction({ type: ACTION_TYPE.EXTEND }),
                      )
                    }
                  >
                    View Change
                  </Button>
                ) : jobDetail.is_paid ? (
                  <Button
                    variant="outlined"
                    onClick={(e) =>
                      onClickButton(
                        e,
                        navigate(APP_ROUTES.JOB.EDIT.to(jobDetail.id)),
                      )
                    }
                  >
                    Edit booking
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={(e) => onClickButton(e, handlePaymentJob)}
                  >
                    {mapButtonTitle[jobDetail.status as JOB_STATUS]}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </StyledJobWrapper>
        <StyledSitterWrapper>
          <Typography className="sitterTitle">
            {SITTER_STATUS_LABEL.accepted}
          </Typography>
          <Stack direction="row" justifyContent={'space-between'} spacing={2}>
            <Stack direction={'row'} spacing={1.5} alignItems="center">
              <img
                src={jobDetail.Sitter.profile_picture}
                alt=""
                className="sitterPicture"
              />
              <Stack>
                <Typography className="sitterName">
                  {toCapitalize(jobDetail.Sitter.User.first_name) +
                    ' ' +
                    toCapitalize(jobDetail.Sitter.User.last_name)}
                </Typography>
                <Typography className="totalExp">
                  Total Exp on App: {jobDetail.total_hours} Hours
                </Typography>
              </Stack>
            </Stack>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(updateACRequestSitterStatus(ACTION_TYPE.ACCEPTED));
                navigate(APP_ROUTES.SITTER_DETAIL.to(jobDetail.sitter_id));
              }}
              sx={{ height: 40 }}
            >
              View
            </Button>
          </Stack>
        </StyledSitterWrapper>
      </StyledContentWrapper>
      {modalAction?.type === ACTION_TYPE.CANCEL && (
        <PopupConfirm
          typeIcon={TYPE_ICON.HELP}
          title="Cancel Booking"
          content={
            <Stack spacing={1.5}>
              <Typography variant="Web_Label_14">
                Are you sure you want to cancel this booking?
              </Typography>
              {/* {isBefore24h(jobDetail?.start_datetime) && (
                <Typography variant="Web_Label_14">
                  A&nbsp;
                  <Typography variant="Web_Label_14" fontWeight={700}>
                    1 hour cancellation fee
                  </Typography>
                  &nbsp;will be applied for sits that are cancelled 24 hours
                  before the sit.
                </Typography>
              )} */}
            </Stack>
          }
          textButton2="Back"
          onSubmit={onSubmitCallback}
          handleClose={onCloseModalAction}
        />
      )}
      {modalAction?.type === ACTION_TYPE.CANCELLED && (
        <PopupConfirm
          typeIcon={TYPE_ICON.CHECK}
          title={SYS_MESS.SUCCESS.BOOKING_CANCELED}
          content={
            <>
              <Typography variant="Web_Label_14"></Typography>
              <Typography variant="Web_Label_14">
                Your trial has been canceled. You can find your credits in your
                Aunty account and use them for future bookings.
              </Typography>
            </>
          }
          textButton="Back to Bookings"
          handleClose={onCloseModalAction}
        />
      )}
      {modalAction?.type === ACTION_TYPE.EXTEND && (
        <AcceptExtensionReductionModal
          dataJob={jobDetail}
          _onClose={onCloseModalAction}
        />
      )}
    </StyledBookingDetailWrapper>
  );
}

export default JobDetail;
